import { supabase } from "@/integrations/supabase/client";

export interface Event {
    id: string;
    page_slug: string;
    banner_image_url: string | null;
    wysiwyg_content: string | null;
    selected_genres: string[];
    start_date: string;
    end_date: string;
    display_on_dashboard: boolean;
    is_active: boolean;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface Game {
    id: string;
    title: string;
    description: string;
    genres: string[];
    cover_image_url: string | null;
    release_date: string;
    steam_url: string | null;
    available_keys: number;
    allow_requests: boolean;
    developer: {
        id: string;
        display_name: string;
        studio_name?: string;
        studio_slug?: string;
        is_premium: boolean;
    };
}

/**
 * Get event by slug and validate date range
 */
export async function getEventBySlug(slug: string): Promise<Event | null> {
    try {
        const { data, error } = await supabase
            .from("events")
            .select("*")
            .eq("page_slug", slug)
            .eq("is_active", true)
            .single();

        if (error) {
            console.error("Error fetching event:", error);
            return null;
        }

        // Validate date range
        const now = new Date();
        const startDate = new Date(data.start_date);
        const endDate = new Date(data.end_date);

        if (now < startDate || now > endDate) {
            return null;
        }

        return data as Event;
    } catch (error) {
        console.error("Error in getEventBySlug:", error);
        return null;
    }
}

/**
 * Get all active events
 */
export async function getActiveEvents(): Promise<Event[]> {
    try {
        const now = new Date().toISOString();
        const { data, error } = await supabase
            .from("events")
            .select("*")
            .eq("is_active", true)
            .lte("start_date", now)
            .gte("end_date", now)
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Error fetching active events:", error);
            return [];
        }

        return (data || []) as Event[];
    } catch (error) {
        console.error("Error in getActiveEvents:", error);
        return [];
    }
}

/**
 * Get events to display on dashboard
 */
export async function getDashboardEvents(): Promise<Event[]> {
    try {
        const now = new Date().toISOString();
        const { data, error } = await supabase
            .from("events")
            .select("*")
            .eq("is_active", true)
            .eq("display_on_dashboard", true)
            .lte("start_date", now)
            .gte("end_date", now)
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Error fetching dashboard events:", error);
            return [];
        }

        // Filter events to only include those with at least 3 eligible games
        const eventsWithGames = await Promise.all(
            (data || []).map(async (event) => {
                const games = await getEventGames(event.selected_genres);
                return games.length >= 3 ? event : null;
            })
        );

        // Remove null values (events without enough games)
        return eventsWithGames.filter((event): event is Event => event !== null);
    } catch (error) {
        console.error("Error in getDashboardEvents:", error);
        return [];
    }
}

/**
 * Get games for an event (premium studios with matching genres)
 */
export async function getEventGames(selectedGenres: string[]): Promise<Game[]> {
    try {
        const { data, error } = await supabase
            .from("games")
            .select(`
        *,
        developer:profiles!games_developer_id_fkey(
          id,
          display_name,
          studio_name,
          studio_slug,
          is_premium
        )
      `)
            .eq("is_published", true)
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Error fetching event games:", error);
            return [];
        }

        // Filter for premium studios and matching genres
        const filteredGames = (data || [])
            .filter((game: any) => {
                // Check if developer is premium
                if (!game.developer?.is_premium) return false;

                // Parse game genres
                const gameGenres = game.genre
                    ? game.genre.split(',').map((g: string) => g.trim())
                    : [];

                // Check if any game genre matches selected genres
                return selectedGenres.some(selectedGenre =>
                    gameGenres.includes(selectedGenre)
                );
            })
            .map((game: any) => ({
                ...game,
                genres: game.genre ? game.genre.split(',').map((g: string) => g.trim()) : [],
            }));

        return filteredGames as Game[];
    } catch (error) {
        console.error("Error in getEventGames:", error);
        return [];
    }
}

/**
 * Upload event banner image
 */
export async function uploadEventBanner(file: File, eventSlug: string): Promise<string | null> {
    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${eventSlug}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('event-banners')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (uploadError) {
            console.error("Error uploading banner:", uploadError);
            return null;
        }

        const { data } = supabase.storage
            .from('event-banners')
            .getPublicUrl(filePath);

        return data.publicUrl;
    } catch (error) {
        console.error("Error in uploadEventBanner:", error);
        return null;
    }
}

/**
 * Delete event banner image
 */
export async function deleteEventBanner(bannerUrl: string): Promise<boolean> {
    try {
        // Extract file path from URL
        const urlParts = bannerUrl.split('/event-banners/');
        if (urlParts.length < 2) return false;

        const filePath = urlParts[1];

        const { error } = await supabase.storage
            .from('event-banners')
            .remove([filePath]);

        if (error) {
            console.error("Error deleting banner:", error);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error in deleteEventBanner:", error);
        return false;
    }
}
